import { useMemo, useState } from "react";
import { Modal } from "../components/Modal";
import { SearchInput } from "../components/SearchInput";
import { Segmented } from "../components/charts";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  LoadingBlock,
  PageHeader,
  Table,
  Tag,
  Td,
  Th,
  cx,
} from "../components/ui";
import { useAuth } from "../auth/AuthProvider";
import { ApiError } from "../lib/api";
import { formatRelative } from "../lib/format";
import { money, moneyCompact } from "../lib/money";
import {
  campaignState,
  conversionRate,
  describeOffer,
  isDeletable,
  quotaUsed,
  remainingExposureMicros,
  totalExposure,
  type CampaignState,
} from "../lib/promotions";
import {
  useDeletePromoCampaign,
  usePromoCampaigns,
  usePromoRedemptions,
  useUpdatePromoCampaign,
} from "../lib/queries";
import type { PromoCampaign } from "../lib/types";
import { PromoCampaignForm } from "./PromoCampaignForm";

/**
 * Credit promotions.
 *
 * Built around the question a campaign row cannot answer on its own: **what am I
 * exposed to?** `$300` and a quota of `1,000` are two innocuous fields whose
 * product is a $300,000 commitment, and no amount of table formatting makes an
 * operator multiply per row before clicking Activate. So the total is computed
 * and put at the top, and the per-campaign figure sits in the row.
 *
 * The second question is whether an offer is working, which needs conversion
 * beside the claim count rather than behind a modal — `redemptions` counts
 * clicks and `paidMicros` counts money, and for a match campaign they are
 * unrelated until somebody pays.
 *
 * Filters exist because the list is deliberately unfiltered by the API: this is
 * where a draft is written and where last quarter's offer is looked up, so it
 * accumulates. Defaulting the filter to "live" would hide exactly that history;
 * defaulting to "all" and offering one click to the live ones does not.
 */
const STATE_FILTERS: { value: CampaignState | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "live", label: "Live" },
  { value: "scheduled", label: "Scheduled" },
  { value: "inactive", label: "Off" },
  { value: "ended", label: "Ended" },
];

/** The API's own ceiling on a list page. Asking for more is refused outright. */
const PAGE_SIZE = 100;

const STATE_TAGS: Record<CampaignState, { tone: "good" | "warn" | "neutral"; label: string }> = {
  live: { tone: "good", label: "Live" },
  scheduled: { tone: "warn", label: "Scheduled" },
  "fully-claimed": { tone: "warn", label: "Fully claimed" },
  ended: { tone: "neutral", label: "Ended" },
  inactive: { tone: "neutral", label: "Off" },
};

export function PromotionsPage() {
  const { role } = useAuth();
  const canWrite = role === "superadmin";

  /*
   * 100 is the API's ceiling — `listQuerySchema` caps it there, and asking for
   * 200 had the whole request rejected, so this page rendered "No campaigns"
   * over a table that was not empty. Filters and search run here rather than
   * server-side, so this is also the window they operate on.
   */
  const campaigns = usePromoCampaigns({ limit: PAGE_SIZE });
  const update = useUpdatePromoCampaign();
  const remove = useDeletePromoCampaign();

  const [editing, setEditing] = useState<PromoCampaign | null>(null);
  const [creating, setCreating] = useState(false);
  const [inspecting, setInspecting] = useState<PromoCampaign | null>(null);
  const [deleting, setDeleting] = useState<PromoCampaign | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<CampaignState | "all">("all");
  const [search, setSearch] = useState("");

  const all = campaigns.data?.data ?? [];
  const truncated = (campaigns.data?.total ?? 0) > all.length;

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return all.filter((campaign) => {
      if (filter !== "all") {
        const state = campaignState(campaign);
        // "Fully claimed" is a live campaign that has run out, so it belongs
        // under Live rather than in a bucket of its own — an operator filtering
        // for what is switched on wants to see it.
        const bucket = state === "fully-claimed" ? "live" : state;
        if (bucket !== filter) return false;
      }
      if (!needle) return true;
      return (
        campaign.code.toLowerCase().includes(needle) ||
        campaign.name.toLowerCase().includes(needle)
      );
    });
  }, [all, filter, search]);

  const exposure = useMemo(() => totalExposure(all), [all]);
  const liveCount = useMemo(
    () => all.filter((campaign) => campaignState(campaign) === "live").length,
    [all],
  );
  const paidTotal = useMemo(
    () => all.reduce((sum, campaign) => sum + campaign.paidMicros, 0),
    [all],
  );

  async function toggle(campaign: PromoCampaign) {
    setError(null);
    try {
      await update.mutateAsync({ id: campaign.id, active: !campaign.active });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not change that campaign.");
    }
  }

  async function destroy(campaign: PromoCampaign) {
    setError(null);
    try {
      await remove.mutateAsync(campaign.id);
      setDeleting(null);
    } catch (cause) {
      /*
       * Surfaced on the page rather than inside the dialog, and the dialog
       * closes with it. The refusal that matters here — a campaign somebody has
       * redeemed — is the API's, and it explains what to do instead; leaving the
       * dialog open over it would put a Delete button under a message saying
       * this cannot be deleted.
       */
      setDeleting(null);
      setError(cause instanceof ApiError ? cause.message : "Could not delete that campaign.");
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Promotions"
        description="Credit codes. Never spendable on AI inference."
        action={canWrite ? <Button onClick={() => setCreating(true)}>New campaign</Button> : undefined}
      />

      {error && <Alert>{error}</Alert>}

      {/*
        Said when the tiles below are computed over part of the set.

        The exposure figure is money, and a total derived from the first hundred
        of a hundred and fifty is wrong while looking right. Rather than hide
        that behind a silent cap, the page says which campaigns it counted.
      */}
      {truncated && (
        <Alert tone="warning">
          Showing the {PAGE_SIZE} newest of {campaigns.data?.total} campaigns. The figures below
          cover only those.
        </Alert>
      )}

      {/*
        Three figures, and the third is the reason this strip exists. Committed
        spend is history; exposure is what has not happened yet and can still be
        stopped, which makes it the only one of the three that is actionable.

        The tiles carry no explanatory footnote. What each figure means belongs in
        OPERATIONS, and a console that re-teaches its own numbers on every visit
        is a console an operator reads past.
      */}
      <section className="grid gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-3">
        <Figure label="Live" value={String(liveCount)} detail={`of ${all.length}`} />
        <Figure label="Paid out" value={moneyCompact(paidTotal)} detail="all time" />
        <Figure
          label="Exposure"
          value={exposure.unbounded ? `${moneyCompact(exposure.micros)}+` : moneyCompact(exposure.micros)}
          detail={exposure.unbounded ? "uncapped campaign" : "if fully claimed"}
          tone={exposure.unbounded || exposure.micros > 0 ? "warn" : "neutral"}
        />
      </section>

      <Card>
        <CardHeader
          title="Campaigns"
          action={
            <div className="flex flex-wrap items-center gap-2">
              <SearchInput value={search} onChange={setSearch} placeholder="Code or name…" />
              <Segmented
                label="Filter by state"
                options={STATE_FILTERS}
                value={filter}
                onChange={setFilter}
              />
            </div>
          }
        />

        {campaigns.isLoading ? (
          <LoadingBlock />
        ) : campaigns.isError ? (
          /*
           * A failed read, said as one.
           *
           * This branch is the actual lesson of the bug that prompted it: the
           * page asked for a limit the API refuses, the request was rejected,
           * and `data` stayed undefined — so the empty state below rendered "No
           * campaigns" over a table that was full. An error that presents as
           * absence is worse than an error, because nobody goes looking.
           */
          <Alert>
            Could not load campaigns.{" "}
            {campaigns.error instanceof ApiError ? campaigns.error.message : "Try again."}
          </Alert>
        ) : all.length === 0 ? (
          <EmptyState
            title="No campaigns"
            description="Nothing is granted automatically."
            action={canWrite ? <Button onClick={() => setCreating(true)}>New campaign</Button> : undefined}
          />
        ) : rows.length === 0 ? (
          <p className="p-5 text-sm text-fg-subtle">Nothing matches that filter.</p>
        ) : (
          <Table
            head={
              <tr>
                <Th>Code</Th>
                <Th priority="md">Offer</Th>
                <Th numeric>Claims</Th>
                <Th numeric priority="md">Converted</Th>
                <Th numeric>Paid out</Th>
                <Th numeric priority="md">Exposure</Th>
                <Th />
              </tr>
            }
          >
            {rows.map((campaign) => {
              const state = campaignState(campaign);
              const tag = STATE_TAGS[state];
              const used = quotaUsed(campaign);
              const conversion = conversionRate(campaign);
              const exposed = remainingExposureMicros(campaign);

              return (
                <tr key={campaign.id}>
                  <Td>
                    <div className="flex flex-wrap items-center gap-2">
                      <code className="ident text-fg">{campaign.code}</code>
                      <Tag tone={tag.tone}>{tag.label}</Tag>
                      {campaign.newMembersOnly && <Tag tone="neutral">New only</Tag>}
                      {/*
                        Accent, not neutral, because these two are the
                        properties of a campaign that are visible *outside* the
                        platform. Every other tag here describes how a code
                        behaves once somebody has it; these say who can get it
                        in the first place.

                        Both are shown because they are separately decided and
                        separately consequential. "On website" is one campaign
                        at a time, grants only, seen by visitors who may redeem
                        it once. "With affiliates" is any number of campaigns,
                        either kind, handed to people whose work is publishing
                        codes — the larger exposure of the two, and the one an
                        operator scanning this list is least likely to remember
                        setting.
                      */}
                      {campaign.featuredOnWeb && <Tag tone="accent">On website</Tag>}
                      {campaign.featuredForAffiliates && <Tag tone="accent">With affiliates</Tag>}
                      {/*
                        Warn, not neutral, and shown only when set — like the
                        website tag above, this is a property with a consequence
                        rather than a description. Credit from this campaign can
                        become supplier spend, and it opens AI for accounts that
                        have never paid, so an operator scanning the list for
                        "why is this account calling models" should find it here.
                      */}
                      {campaign.spendableOn === "all" && <Tag tone="warn">Pays for AI</Tag>}
                    </div>
                    <p className="mt-0.5 text-xs text-fg-subtle">{campaign.name}</p>
                  </Td>

                  <Td priority="md" className="text-fg-subtle">
                    {describeOffer(campaign, money)}
                  </Td>

                  <Td numeric>
                    <span className="tabular-nums">
                      {campaign.redemptions.toLocaleString()}
                      {campaign.maxRedemptions !== null && (
                        <span className="text-fg-subtle">
                          {" / "}
                          {campaign.maxRedemptions.toLocaleString()}
                        </span>
                      )}
                    </span>
                    {/*
                      A bar, so a campaign about to run out is visible by scanning
                      instead of by comparing two numbers on every row.
                    */}
                    {used !== null && (
                      <span className="mt-1 block h-1 overflow-hidden rounded-full bg-raised">
                        <span
                          className={cx(
                            "block h-full rounded-full",
                            used >= 1 ? "bg-warn" : "bg-accent/70",
                          )}
                          style={{ width: `${used * 100}%` }}
                        />
                      </span>
                    )}
                    {campaign.pendingCount > 0 && (
                      <span className="mt-1 block text-xs text-fg-subtle">
                        {campaign.pendingCount} awaiting payment
                      </span>
                    )}
                  </Td>

                  <Td numeric priority="md">
                    {/*
                      Null rather than 0% on an untried campaign: "nobody has
                      redeemed it" and "everybody who did left" are different
                      statements and only one of them condemns the offer.
                    */}
                    {conversion === null ? (
                      <span className="text-fg-subtle">—</span>
                    ) : (
                      <span
                        className={cx(
                          "tabular-nums",
                          conversion >= 0.2 ? "text-mint" : undefined,
                        )}
                      >
                        {Math.round(conversion * 100)}%
                        <span className="block text-xs text-fg-subtle">
                          {campaign.convertedCount} paid
                        </span>
                      </span>
                    )}
                  </Td>

                  <Td numeric>{money(campaign.paidMicros)}</Td>

                  <Td numeric priority="md">
                    {exposed === null ? (
                      <span className="text-warn">unbounded</span>
                    ) : exposed === 0 ? (
                      <span className="text-fg-subtle">—</span>
                    ) : (
                      <span className="tabular-nums">{money(exposed)}</span>
                    )}
                  </Td>

                  <Td>
                    <div className="flex flex-wrap justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setInspecting(campaign)}>
                        Redemptions
                      </Button>
                      {canWrite && (
                        <>
                          <Button variant="secondary" size="sm" onClick={() => setEditing(campaign)}>
                            Edit
                          </Button>
                          <Button
                            variant={campaign.active ? "secondary" : "primary"}
                            size="sm"
                            loading={update.isPending}
                            onClick={() => void toggle(campaign)}
                          >
                            {campaign.active ? "Turn off" : "Activate"}
                          </Button>
                          {/*
                            Only where it can succeed. The API refuses to delete
                            a campaign anybody has redeemed — those rows are what
                            explain the promotional credit on customers' ledgers
                            — so a button that is always refused would be a
                            button that teaches operators to ignore errors.

                            Deactivation is the answer for everything else, which
                            is why this sits last and quiet: a draft written by
                            mistake is the case it is for, not "retire this
                            offer".
                          */}
                          {isDeletable(campaign) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-danger"
                              onClick={() => setDeleting(campaign)}
                            >
                              Delete
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </Table>
        )}
      </Card>

      <Modal
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        title={editing ? `Edit ${editing.code}` : "New campaign"}
        description={editing ? "Code and kind are fixed." : "Saved inactive. Activate from the list."}
      >
        <PromoCampaignForm
          campaign={editing}
          onDone={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      </Modal>

      <Modal
        open={inspecting !== null}
        onClose={() => setInspecting(null)}
        title={inspecting ? `${inspecting.code} redemptions` : ""}
      >
        {inspecting && <RedemptionList campaignId={inspecting.id} />}
      </Modal>

      {/*
        A confirmation, because delete is the one action on this page that cannot
        be undone — everything else is a flag an operator can set back. A modal
        rather than `window.confirm`: the native dialog blocks the page, cannot
        name what is being deleted in the product's own words, and looks like a
        browser warning rather than a decision the console asked for.
      */}
      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title={deleting ? `Delete ${deleting.code}?` : ""}
        description="Nobody has redeemed this campaign, so nothing on a customer's ledger points at it."
      >
        {deleting && (
          <div className="space-y-4">
            <p className="text-sm text-fg-muted">
              <span className="font-medium text-fg">{deleting.name}</span> and its code are
              removed for good. If you only want to stop it being redeemable, turn it off
              instead — that keeps the record.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDeleting(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={remove.isPending}
                onClick={() => void destroy(deleting)}
              >
                Delete campaign
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/** A summary tile. Mirrors the Revenue page's, without its footnote row. */
function Figure({
  label,
  value,
  detail,
  tone = "neutral",
}: {
  label: string;
  value: string;
  detail: string;
  tone?: "neutral" | "warn";
}) {
  return (
    <div className="bg-surface px-5 py-4">
      <p className="text-sm font-medium text-fg-subtle">{label}</p>
      <p
        className={cx(
          "mt-2 text-2xl font-semibold tracking-tight tabular-nums",
          tone === "warn" ? "text-warn" : undefined,
        )}
      >
        {value}
      </p>
      <p className="mt-1 text-xs text-fg-subtle">{detail}</p>
    </div>
  );
}

/**
 * Who redeemed, and the column that says whether it worked.
 *
 * The list rather than the total, because a campaign's headline figures cannot
 * distinguish a hundred people who took free credit and left from ten who took
 * it and stayed, and only one of those is worth running again.
 */
function RedemptionList({ campaignId }: { campaignId: string }) {
  const redemptions = usePromoRedemptions(campaignId, { limit: 100 });
  const rows = redemptions.data?.data ?? [];

  if (redemptions.isLoading) return <LoadingBlock />;
  // Same guard as the campaign list: "nobody redeemed it" and "the read failed"
  // look identical otherwise, and only one of them is about the campaign.
  if (redemptions.isError) return <Alert>Could not load redemptions.</Alert>;
  if (rows.length === 0) {
    return <p className="text-sm text-fg-subtle">Nobody has redeemed this code yet.</p>;
  }

  const converted = rows.filter((row) => row.converted).length;

  return (
    <div className="space-y-4">
      <p className="text-sm text-fg-muted">
        <span className="font-medium text-fg">
          {converted} of {rows.length}
        </span>{" "}
        converted.
      </p>

      <Table
        head={
          <tr>
            <Th>Organization</Th>
            <Th priority="md">Redeemed</Th>
            <Th numeric>Paid out</Th>
            <Th>Converted</Th>
          </tr>
        }
      >
        {rows.map((row) => (
          <tr key={row.id}>
            <Td>
              <span className="text-fg">{row.organization.name}</span>
              {row.userEmail && (
                <span className="block text-xs text-fg-subtle">{row.userEmail}</span>
              )}
            </Td>
            <Td priority="md" className="text-fg-subtle">
              {formatRelative(row.redeemedAt)}
            </Td>
            <Td numeric>
              {row.paidMicros === null ? (
                // A match claim with no payment yet. Not zero — zero would say
                // the bonus was calculated and came to nothing.
                <span className="text-fg-subtle">pending</span>
              ) : (
                money(row.paidMicros)
              )}
            </Td>
            <Td>{row.converted ? <Tag tone="good">Paid</Tag> : <Tag tone="neutral">No</Tag>}</Td>
          </tr>
        ))}
      </Table>
    </div>
  );
}
